import React from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { cn } from '../../../lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFileSelected: (file: File) => void;
}

/** Import d'un planning depuis Excel/CSV (glisser-déposer ou sélection). */
const ImportHeatsDialog: React.FC<Props> = ({ open, onOpenChange, onFileSelected }) => {
  const { t } = useTranslation();
  // État local : le survol de la zone de dépôt n'intéresse personne d'autre.
  const [dragOver, setDragOver] = React.useState(false);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelected(file);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('heats.importDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
              dragOver ? "border-foreground bg-white/[0.06]" : "border-muted-foreground/30 hover:border-foreground/50"
            )}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onClick={() => document.getElementById('heat-file-input')?.click()}
          >
            <Upload size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-1">{t('heats.importDialog.dropzone')}</p>
            <p className="text-sm text-muted-foreground">{t('heats.importDialog.dropzoneHint')}</p>
            <input
              id="heat-file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFileSelected(file);
              }}
            />
          </div>

          <Alert variant="info">
            <AlertDescription>
              <p className="font-bold mb-2">{t('heats.importDialog.formatExpected')}</p>
              <p className="text-sm">{t('heats.importDialog.formatDesc')}</p>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('heats.actions.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportHeatsDialog;
